import type {
  DataTag,
  InfiniteData,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import { Article } from '@repo/types';

import customInstance, { ErrorType } from '../mutator/custom-instance';
import {
  ArticlesArray,
  ArticlesByBrandArray,
  ArticlesFeedArray,
  CategoryArray,
  CategoryEnumArray,
  ListArticleParams,
  ListArticlesByBrandParams,
  ListCategoriesEnumParams,
  ListCategoriesParams,
  ShowArticleByIdParams,
  ShowArticleBySlugParams,
  ShowArticleByUrlParams,
  ShowArticleSharingUrlByIdParams,
  ShowArticleSharingUrlByIdRes,
  ShowRandomArticleUrlParams,
  ShowRandomArticleUrlRes,
} from './model';

/**
 * @summary List articles
 */
export const listArticles = async (
  params?: ListArticleParams,
  signal?: AbortSignal,
) => {
  return customInstance<ArticlesArray>({
    url: `/articles`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListArticlesQueryKey = (params?: ListArticleParams) => {
  return [`/article`, ...(params ? [params] : [])] as const;
};

export const getListArticlesQueryOptions = <
  TData = Awaited<ReturnType<typeof listArticles>>,
  TError = ErrorType<Error>,
>(
  params?: ListArticleParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listArticles>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListArticlesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listArticles>>> = ({
    signal,
  }) => listArticles(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listArticles>>,
    TError,
    TData
  > & {
    queryKey: DataTag<QueryKey, TData>;
  };
};

export function useListArticles<
  TData = Awaited<ReturnType<typeof listArticles>>,
  TError = ErrorType<Error>,
>(
  params?: ListArticleParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listArticles>>, TError, TData>
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getListArticlesQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List articles with infinite pagination
 */
export const getListArticlesInfiniteQueryOptions = <
  TData = InfiniteData<
    Awaited<ReturnType<typeof listArticles>>,
    ListArticleParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListArticleParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listArticles>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listArticles>>,
        QueryKey,
        ListArticleParams
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListArticlesQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listArticles>>,
    QueryKey,
    ListArticleParams
  > = ({ signal, pageParam }) =>
    listArticles({ ...params, ...pageParam }, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseInfiniteQueryOptions<
    Awaited<ReturnType<typeof listArticles>>,
    TError,
    TData,
    Awaited<ReturnType<typeof listArticles>>,
    QueryKey,
    ListArticleParams
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useListArticlesInfinite<
  TData = InfiniteData<
    Awaited<ReturnType<typeof listArticles>>,
    ListArticleParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListArticleParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listArticles>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listArticles>>,
        QueryKey,
        ListArticleParams
      >
    >;
  },
): UseInfiniteQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
} {
  const queryOptions = getListArticlesInfiniteQueryOptions(params, options);

  const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List articles feed
 */
export const listArticlesFeed = async (
  params?: ListArticleParams,
  signal?: AbortSignal,
) => {
  return customInstance<ArticlesFeedArray>({
    url: `/articles/feed`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListArticlesFeedQueryKey = (params?: ListArticleParams) => {
  return [`/article/feed`, ...(params ? [params] : [])] as const;
};

export const getListArticlesFeedQueryOptions = <
  TData = Awaited<ReturnType<typeof listArticlesFeed>>,
  TError = ErrorType<Error>,
>(
  params?: ListArticleParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listArticlesFeed>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListArticlesFeedQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listArticlesFeed>>
  > = ({ signal }) => listArticlesFeed(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listArticlesFeed>>,
    TError,
    TData
  > & {
    queryKey: DataTag<QueryKey, TData>;
  };
};

export function useListArticlesFeed<
  TData = Awaited<ReturnType<typeof listArticlesFeed>>,
  TError = ErrorType<Error>,
>(
  params?: ListArticleParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listArticlesFeed>>,
        TError,
        TData
      >
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getListArticlesFeedQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List articles feed with infinite pagination
 */
export const getListArticlesFeedInfiniteQueryOptions = <
  TData = InfiniteData<
    Awaited<ReturnType<typeof listArticlesFeed>>,
    ListArticleParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListArticleParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listArticlesFeed>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listArticlesFeed>>,
        QueryKey,
        ListArticleParams
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListArticlesFeedQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listArticlesFeed>>,
    QueryKey,
    ListArticleParams
  > = ({ signal, pageParam }) =>
    listArticlesFeed({ ...params, ...pageParam }, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseInfiniteQueryOptions<
    Awaited<ReturnType<typeof listArticlesFeed>>,
    TError,
    TData,
    Awaited<ReturnType<typeof listArticlesFeed>>,
    QueryKey,
    ListArticleParams
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useListArticlesFeedInfinite<
  TData = InfiniteData<
    Awaited<ReturnType<typeof listArticlesFeed>>,
    ListArticleParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListArticleParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listArticlesFeed>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listArticlesFeed>>,
        QueryKey,
        ListArticleParams
      >
    >;
  },
): UseInfiniteQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
} {
  const queryOptions = getListArticlesFeedInfiniteQueryOptions(params, options);

  const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List hackernews articles feed
 */
export const listHackernewsArticlesFeed = async (
  params?: ListArticleParams,
  signal?: AbortSignal,
) => {
  return customInstance<ArticlesFeedArray>({
    url: `/hackernews/feed`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListHackernewsArticlesFeedQueryKey = (
  params?: ListArticleParams,
) => {
  return [`/hackernews/feed`, ...(params ? [params] : [])] as const;
};

export const getListHackernewsArticlesFeedQueryOptions = <
  TData = Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
  TError = ErrorType<Error>,
>(
  params?: ListArticleParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListHackernewsArticlesFeedQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listHackernewsArticlesFeed>>
  > = ({ signal }) => listHackernewsArticlesFeed(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
    TError,
    TData
  > & {
    queryKey: DataTag<QueryKey, TData>;
  };
};

export function useListHackernewsArticlesFeed<
  TData = Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
  TError = ErrorType<Error>,
>(
  params?: ListArticleParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
        TError,
        TData
      >
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getListHackernewsArticlesFeedQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List hackernews articles with infinite pagination
 */
export const getListHackernewsArticlesFeedInfiniteQueryOptions = <
  TData = InfiniteData<
    Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
    ListArticleParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListArticleParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
        QueryKey,
        ListArticleParams
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListHackernewsArticlesFeedQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
    QueryKey,
    ListArticleParams
  > = ({ signal, pageParam }) =>
    listHackernewsArticlesFeed({ ...params, ...pageParam }, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseInfiniteQueryOptions<
    Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
    TError,
    TData,
    Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
    QueryKey,
    ListArticleParams
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useListHackernewsArticlesFeedInfinite<
  TData = InfiniteData<
    Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
    ListArticleParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListArticleParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listHackernewsArticlesFeed>>,
        QueryKey,
        ListArticleParams
      >
    >;
  },
): UseInfiniteQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
} {
  const queryOptions = getListHackernewsArticlesFeedInfiniteQueryOptions(
    params,
    options,
  );

  const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List articles by brand
 */
export const listArticlesByBrand = (
  params?: ListArticlesByBrandParams,
  signal?: AbortSignal,
) => {
  return customInstance<ArticlesByBrandArray>({
    url: `/brands/${params?.id}/articles`,
    method: 'GET',
    params: { ...params, id: undefined },
    signal,
  });
};

export const getListArticlesByBrandQueryKey = (
  params?: ListArticlesByBrandParams,
) => {
  return [
    `/brands/${params?.id}/articles`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListArticlesByBrandQueryOptions = <
  TData = Awaited<ReturnType<typeof listArticlesByBrand>>,
  TError = ErrorType<Error>,
>(
  params?: ListArticlesByBrandParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listArticlesByBrand>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListArticlesByBrandQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listArticlesByBrand>>
  > = ({ signal }) => listArticlesByBrand(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listArticlesByBrand>>,
    TError,
    TData
  > & {
    queryKey: DataTag<QueryKey, TData>;
  };
};

export function useListArticlesByBrand<
  TData = Awaited<ReturnType<typeof listArticlesByBrand>>,
  TError = ErrorType<Error>,
>(
  params?: ListArticlesByBrandParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listArticlesByBrand>>,
        TError,
        TData
      >
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getListArticlesByBrandQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List articles by brand with infinite pagination
 */
export const getListArticlesByBrandInfiniteQueryOptions = <
  TData = InfiniteData<
    Awaited<ReturnType<typeof listArticlesByBrand>>,
    ListArticlesByBrandParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListArticlesByBrandParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listArticlesByBrand>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listArticlesByBrand>>,
        QueryKey,
        ListArticlesByBrandParams
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListArticlesByBrandQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listArticlesByBrand>>,
    QueryKey,
    ListArticlesByBrandParams
  > = ({ signal, pageParam }) =>
    listArticlesByBrand({ ...params, ...pageParam }, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseInfiniteQueryOptions<
    Awaited<ReturnType<typeof listArticlesByBrand>>,
    TError,
    TData,
    Awaited<ReturnType<typeof listArticlesByBrand>>,
    QueryKey,
    ListArticlesByBrandParams
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useListArticlesByBrandInfinite<
  TData = InfiniteData<
    Awaited<ReturnType<typeof listArticlesByBrand>>,
    ListArticlesByBrandParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListArticlesByBrandParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listArticlesByBrand>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listArticlesByBrand>>,
        QueryKey,
        ListArticlesByBrandParams
      >
    >;
  },
): UseInfiniteQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
} {
  const queryOptions = getListArticlesByBrandInfiniteQueryOptions(
    params,
    options,
  );

  const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Info for a specific article
 */
export const showArticleByUrl = (
  params: ShowArticleByUrlParams,
  signal?: AbortSignal,
) => {
  return customInstance<Article>({
    url: `/articles/by-url`,
    method: 'GET',
    params,
    signal,
  });
};

export const getShowArticleByUrlQueryKey = (params: ShowArticleByUrlParams) => {
  return [`/articles/by-url`, ...(params ? [params] : [])] as const;
};

export const getShowArticleByUrlQueryOptions = <
  TData = Awaited<ReturnType<typeof showArticleByUrl>>,
  TError = ErrorType<Error>,
>(
  params: ShowArticleByUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof showArticleByUrl>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getShowArticleByUrlQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof showArticleByUrl>>
  > = ({ signal }) => showArticleByUrl(params, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!params.url,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof showArticleByUrl>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useShowArticleByUrl<
  TData = Awaited<ReturnType<typeof showArticleByUrl>>,
  TError = ErrorType<Error>,
>(
  params: ShowArticleByUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof showArticleByUrl>>,
        TError,
        TData
      >
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getShowArticleByUrlQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Info for a specific article
 */
export const showArticleById = async (
  params: ShowArticleByIdParams,
  signal?: AbortSignal,
) => {
  return customInstance<Article>({
    url: `/articles/${params.id}`,
    method: 'GET',
    params,
    signal,
  }).then((res) => res.data);
};

export const getShowArticleByIdQueryKey = (params: ShowArticleByIdParams) => {
  return [`/articles/${params.id}`] as const;
};

export const getShowArticleByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof showArticleById>>,
  TError = ErrorType<Error>,
>(
  params: ShowArticleByIdParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof showArticleById>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getShowArticleByIdQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof showArticleById>>> = ({
    signal,
  }) => showArticleById(params, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!params.id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof showArticleById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useShowArticleById<
  TData = Awaited<ReturnType<typeof showArticleById>>,
  TError = ErrorType<Error>,
>(
  params: ShowArticleByIdParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof showArticleById>>,
        TError,
        TData
      >
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getShowArticleByIdQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Info for a specific article, by its slug
 */
export const showArticleBySlug = async (
  params: ShowArticleBySlugParams,
  signal?: AbortSignal,
) => {
  return customInstance<Article>({
    url: `/articles/slug/${encodeURIComponent(params.slug)}`,
    method: 'GET',
    signal,
  }).then((res) => res.data);
};

export const getShowArticleBySlugQueryKey = (
  params: ShowArticleBySlugParams,
) => {
  return [`/articles/slug/${params.slug}`] as const;
};

export const getShowArticleBySlugQueryOptions = <
  TData = Awaited<ReturnType<typeof showArticleBySlug>>,
  TError = ErrorType<Error>,
>(
  params: ShowArticleBySlugParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof showArticleBySlug>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getShowArticleBySlugQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof showArticleBySlug>>
  > = ({ signal }) => showArticleBySlug(params, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!params.slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof showArticleBySlug>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useShowArticleBySlug<
  TData = Awaited<ReturnType<typeof showArticleBySlug>>,
  TError = ErrorType<Error>,
>(
  params: ShowArticleBySlugParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof showArticleBySlug>>,
        TError,
        TData
      >
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getShowArticleBySlugQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Show random article url
 */
export const showRandomArticleUrl = (
  params?: ShowRandomArticleUrlParams,
  signal?: AbortSignal,
) => {
  return customInstance<ShowRandomArticleUrlRes>({
    url: `/articles/random`,
    method: 'GET',
    params,
    signal,
  });
};

export const getShowRandomArticleUrlQueryKey = (
  params?: ShowRandomArticleUrlParams,
) => {
  return [`/articles/random`, ...(params ? [params] : [])] as const;
};

export const getShowRandomArticleUrlQueryOptions = <
  TData = Awaited<ReturnType<typeof showRandomArticleUrl>>,
  TError = ErrorType<Error>,
>(
  params?: ShowRandomArticleUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof showRandomArticleUrl>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getShowRandomArticleUrlQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof showRandomArticleUrl>>
  > = ({ signal }) => showRandomArticleUrl(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof showRandomArticleUrl>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useShowRandomArticleUrl<
  TData = Awaited<ReturnType<typeof showRandomArticleUrl>>,
  TError = ErrorType<Error>,
>(
  params?: ShowRandomArticleUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof showRandomArticleUrl>>,
        TError,
        TData
      >
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getShowRandomArticleUrlQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Show article sharing url by id
 */
export const showArticleSharingUrlById = (
  params?: ShowArticleSharingUrlByIdParams,
  signal?: AbortSignal,
) => {
  return customInstance<ShowArticleSharingUrlByIdRes>({
    url: `/articles/${params?.id}/sharing`,
    method: 'GET',
    params,
    signal,
  });
};

export const getShowArticleSharingUrlByIdMutationOptions = <
  TError = ErrorType<Error>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof showArticleSharingUrlById>>,
    TError,
    { data: ShowArticleSharingUrlByIdParams },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof showArticleSharingUrlById>>,
  TError,
  { data: ShowArticleSharingUrlByIdParams },
  TContext
> => {
  const mutationKey = ['showArticleSharingUrlById'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof showArticleSharingUrlById>>,
    { data: ShowArticleSharingUrlByIdParams }
  > = (props) => {
    const { data } = props ?? {};
    return showArticleSharingUrlById(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ShowArticleSharingUrlByIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof showArticleSharingUrlById>>
>;
export type ShowArticleSharingUrlByIdMutationBody =
  ShowArticleSharingUrlByIdParams;
export type ShowArticleSharingUrlByIdMutationError = ErrorType<Error>;

/**
 * @summary Show article sharing url by id
 */
export const useShowArticleSharingUrlById = <
  TError = ErrorType<Error>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof showArticleSharingUrlById>>,
      TError,
      { data: ShowArticleSharingUrlByIdParams },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof showArticleSharingUrlById>>,
  TError,
  { data: ShowArticleSharingUrlByIdParams },
  TContext
> => {
  const mutationOptions = getShowArticleSharingUrlByIdMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary List categories
 */
export const listCategories = async (
  params: ListCategoriesParams,
  signal?: AbortSignal,
) => {
  return customInstance<CategoryArray>({
    url: `/articles/categories`,
    method: 'GET',
    params,
    signal,
  }).then((res) => res.data);
};

export const getListCategoriesQueryKey = (params: ListCategoriesParams) => {
  return [`/articles/categories`, params] as const;
};

export const getListCategoriesQueryOptions = <
  TData = Awaited<ReturnType<typeof listCategories>>,
  TError = ErrorType<Error>,
>(
  params: ListCategoriesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCategories>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCategoriesQueryKey(params);

  const queryFn = ({ signal }: { signal?: AbortSignal }) =>
    listCategories(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listCategories>>,
    TError,
    TData
  >;
};

export function useListCategories<
  TData = Awaited<ReturnType<typeof listCategories>>,
  TError = ErrorType<Error>,
>(
  params: ListCategoriesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCategories>>, TError, TData>
    >;
  },
): UseQueryResult<TData, TError> {
  const queryOptions = getListCategoriesQueryOptions(params, options);

  return useQuery(queryOptions) as UseQueryResult<TData, TError>;
}

/**
 * @summary List categories with infinite pagination
 */
export const getListCategoriesInfiniteQueryOptions = <
  TData = InfiniteData<
    Awaited<ReturnType<typeof listCategories>>,
    ListCategoriesParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListCategoriesParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listCategories>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listCategories>>,
        QueryKey,
        ListCategoriesParams
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCategoriesQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listCategories>>,
    QueryKey,
    ListCategoriesParams
  > = ({ signal, pageParam }) =>
    listCategories({ ...params, ...pageParam }, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseInfiniteQueryOptions<
    Awaited<ReturnType<typeof listCategories>>,
    TError,
    TData,
    Awaited<ReturnType<typeof listCategories>>,
    QueryKey,
    ListCategoriesParams
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useListCategoriesInfinite<
  TData = InfiniteData<
    Awaited<ReturnType<typeof listCategories>>,
    ListCategoriesParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListCategoriesParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listCategories>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listCategories>>,
        QueryKey,
        ListCategoriesParams
      >
    >;
  },
): UseInfiniteQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
} {
  const queryOptions = getListCategoriesInfiniteQueryOptions(params, options);

  const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List categories enum
 */
export const listCategoriesEnum = async (
  params: ListCategoriesEnumParams,
  signal?: AbortSignal,
) => {
  return customInstance<CategoryEnumArray>({
    url: `/categories/enum`,
    method: 'GET',
    params,
    signal,
  }).then((res) => res.data);
};

export const getListCategoriesEnumQueryKey = (
  params: ListCategoriesEnumParams,
) => {
  return [`/articles/categories`, params] as const;
};

export const getListCategoriesEnumQueryOptions = <
  TData = Awaited<ReturnType<typeof listCategoriesEnum>>,
  TError = ErrorType<Error>,
>(
  params: ListCategoriesEnumParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCategoriesEnum>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListCategoriesEnumQueryKey(params);

  const queryFn = ({ signal }: { signal?: AbortSignal }) =>
    listCategoriesEnum(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listCategoriesEnum>>,
    TError,
    TData
  >;
};

export function useListCategoriesEnum<
  TData = Awaited<ReturnType<typeof listCategoriesEnum>>,
  TError = ErrorType<Error>,
>(
  params: ListCategoriesEnumParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listCategoriesEnum>>,
        TError,
        TData
      >
    >;
  },
): UseQueryResult<TData, TError> {
  const queryOptions = getListCategoriesEnumQueryOptions(params, options);

  return useQuery(queryOptions) as UseQueryResult<TData, TError>;
}
